import React, { Suspense, useMemo } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Navigate, RouteProps } from 'react-router-dom';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { Role } from 'utils/helpers/auth0.helpers';
import { useAuth0User } from 'hooks/useAuth0User';
import { FeatureFlag /*, featureFlagIsTrue*/ } from 'utils/helpers/featureFlag.helpers';
import { isSupplier, userHasPermissions } from 'utils/helpers/permissions.helpers';
import { UserAuthState } from 'typings/AppRoute';
import Routes from 'routes';

type SecureRouteProps = RouteProps & {
  component: React.FC;
  roles?: Role[];
  featureFlag?: FeatureFlag;
  userAuthState?: UserAuthState;
};

/**
 * 1. Uses the withAuthenticationRequired HOC provided by @auth0/auth0-react to ensure that the user is authenticated.
 * 2. Checks that the user has the required roles to access this route (authorization).
 * 3. Checks that this feature is enabled for the user (feature flagging).
 * IF criteria are not met, user is redirected to 404
 */
const SecureRoute: React.FC<SecureRouteProps> = ({
  component: Component,
  roles = [],
  userAuthState,
  /*featureFlag,*/ ...rest
}) => {
  const user = useAuth0User();

  const userHasAllowedRole = userHasPermissions(user, roles);

  const canAccess = /*featureFlagIsTrue(featureFlag, user) &&*/ userHasAllowedRole;

  const loading =
    (userAuthState == 'Authenticated' && !user.isAuthenticated) || (userAuthState == 'Logged' && user.isLoading);

  const Cp = useMemo(
    () =>
      withAuthenticationRequired(Component as React.FC<Partial<SecureRouteProps>>, {
        onRedirecting: () => <PageSpinner />,
      }),
    [Component],
  );

  if (loading) {
    return <PageSpinner />;
  }

  return canAccess ? (
    <Suspense fallback={<PageSpinner />}>
      <Cp {...rest} />
    </Suspense>
  ) : (
    // if the current user has a supplier role, redirect them to the orders page
    <Navigate to={isSupplier(user) ? Routes.Orders.path : '/404'} />
  );
};

export default SecureRoute;
